import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateKeyNote } from 'infrastructure/hooks/key-notes/use-update-key-note';
import { useDeleteKeyNote } from 'infrastructure/hooks/key-notes/use-delete-key-note';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import { createPortal } from 'react-dom';

import s from './styles.module.scss';
import { CreateOrUpdateKeyNoteModal } from './modal/create-or-update-key-note';
import { DeleteModalKeyNote } from './modal/delete-key-note';
import { HistoryModalKeyNote } from './modal/history-key-note';
import { KeyNote } from './key-note';

import type { ICreateOrUpdate } from './types';
import type { ModuleType } from 'infrastructure/enums';
import type { IKeyNoteData } from 'infrastructure/interfaces';

interface IKeyNotesContentProps {
  isDataEmpty: boolean;
  data: IKeyNoteData[] | undefined;
  module: ModuleType;
  patientGuid?: string;
  showExpired: boolean;
  onAction?: () => void;
}

const KeyNotesContent: React.FC<IKeyNotesContentProps> = ({
  isDataEmpty,
  data,
  patientGuid,
  module,
  showExpired,
  onAction,
}) => {
  const { t } = useTranslation();
  const [selectedNote, setSelectedNote] = useState<IKeyNoteData | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isDeleteMode, setIsDeleteMode] = useState<boolean>(false);
  const [isHistoryMode, setIsHistoryMode] = useState<boolean>(false);
  const onCancelEdit = () => setIsEditMode(false);
  const onCancelDelete = () => setIsDeleteMode(false);
  const { updateKeyNote, isLoading } = useUpdateKeyNote({ module });
  const { deleteKeyNote, isLoading: deleteIsLoading } = useDeleteKeyNote({
    module,
  });

  const onSave = async (values: ICreateOrUpdate) => {
    if (patientGuid && selectedNote?.guid) {
      updateKeyNote(
        {
          patientGuid,
          module,
          note: values.note,
          expiresAt: values.expiresAt,
          noteGuid: selectedNote.guid,
          showExpired,
        },
        {
          onSuccess() {
            notificationController.success({
              message: t('messages.keynoteUpdated'),
            });
            setIsEditMode(false);
            onAction?.();
          },
        },
      );
    }
    if (isLoading) {
      setIsEditMode(false);
    }
  };

  const handleDeleteNote = (guid: string) => {
    if (selectedNote?.guid) {
      deleteKeyNote(
        {
          patientGuid: guid,
          noteGuid: selectedNote.guid,
          module,
          showExpired,
        },
        {
          onSuccess() {
            notificationController.info({
              message: t('messages.keynoteDeleted'),
            });
            setIsDeleteMode(false);
            onAction?.();
          },
        },
      );
      if (deleteIsLoading) {
        setIsDeleteMode(false);
      }
    }
  };

  useEffect(() => {
    setSelectedNote(null);
  }, [module]);

  return (
    <>
      {selectedNote && isHistoryMode && patientGuid && (
        <HistoryModalKeyNote
          module={module}
          note={selectedNote}
          isOpen={isHistoryMode}
          patientGuid={patientGuid}
          onCancel={() => setIsHistoryMode(false)}
        />
      )}
      {selectedNote && patientGuid && (
        <DeleteModalKeyNote
          isLoading={deleteIsLoading}
          onCancel={onCancelDelete}
          onOk={() => handleDeleteNote(patientGuid)}
          isOpen={isDeleteMode}
        />
      )}
      {createPortal(
        <CreateOrUpdateKeyNoteModal
          onCancel={onCancelEdit}
          moduleType={module}
          isOpen={isEditMode}
          isLoading={isLoading}
          note={selectedNote}
          onSave={onSave}
        />,
        document.body,
      )}
      {isDataEmpty ? (
        <div data-cy="patient-key-note-content" className={s['empty-note']}>
          {t('labels.noNotes')}
        </div>
      ) : (
        data?.map((keynote) => (
          <div key={`${keynote.guid}-keynote-${module}`}>
            <KeyNote
              showActions
              keynote={keynote}
              onEdit={() => {
                setIsEditMode(true);
                setSelectedNote(keynote);
              }}
              onShowHistory={() => {
                setIsHistoryMode(true);
                setSelectedNote(keynote);
              }}
              onDelete={() => {
                setSelectedNote(keynote);
                setIsDeleteMode(true);
              }}
            />
          </div>
        ))
      )}
    </>
  );
};

export default KeyNotesContent;
